import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';

import DecisionModal from '@/components/custom/InfoPopups/DecisionModal';
import ErrorPopup from '@/components/custom/InfoPopups/ErrorPopup';
import SuccessPopup from '@/components/custom/InfoPopups/SuccessPopup';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { DEFAULT_CRM_SYSTEM } from '@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen';
import { AddQuestionModal } from '@/features/question/components/organisms/AddQuestionModal';
import { EditQuestionModal } from '@/features/question/components/organisms/EditQuestionModal';
// import { SurveyRow } from "@/features/surveys/components/organisms/SurveyRow";

import { QuestionRow } from '@/features/question/components/organisms/QuestionRow';

import { SelectCrmSystemSanitizedCombobox } from "@/features/crm_system/components/molecules/SelectCrmSystemSanitizedCombobox";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { useAddSurveyToSurveyBank } from '@/features/survey_bank/hooks/useMutation/useAddSurveyToSurveyBank';
import { useEditSurveyInSurveyBank } from '@/features/survey_bank/hooks/useMutation/useEditSurveyInSurveyBank';
import { useGetSingleSurveyFromBank } from '@/features/survey_bank/hooks/useQuery/useGetSingleSurveyFromBank';
import { useAddQuestionsToSurveyBankQuestionBank } from '@/lib/useQuery/surveyBankQuestionBank/useAddQuestionsToSurveyBankQuestionBank';
import { useDeleteQuestionsFromSurveyBankQuestionBank } from '@/lib/useQuery/surveyBankQuestionBank/useDeleteSelectedQuestionsFromSurveyBankQuestionBank';
import { prepareQuestionsWithNewIds } from "@/lib/utils/replaceOldIdsWithNewIds";
import { useOrganizationStore } from '@/lib/zustand/useStore';
import { Loader } from 'lucide-react';
import { useParams } from 'react-router-dom';

import { NavigationBarBreadCrumbs } from '@/components/molecules/BackNav';
import { useSyncSurveysWithTemplate } from '@/features/survey_sync/hooks/useSyncSurveysWithTemplate';
import { useCheckSyncSurveysWithTemplate } from '@/features/survey_sync/hooks/useCheckSyncSurveysWithTemplate';
import { dangerousSyncDialog } from '@/features/survey_sync/components/organisms/DangerousSyncDialog';
import { CrmSystemIds } from '@/features/crm_system/lib/_core/crmSystem/crmSystem.types.gen';
import { groupQuestionsByDependencies } from '@/features/question/hooks/useQuery/useGetQuestionsBySurveyId/utils/groupQuestionsByDependencies';
import { useSaveSurveyAsTemplate } from '@/features/survey_bank/hooks/useMutation/useSaveSurveyAsTemplate';

export function SurveyTemplate() {
    const { surveyId } = useParams();

    const navigate = useNavigate();
    const [showAddModal, setShowAddModal] = useState(false);
    const [surveyQuestionsData, setSurveyQuestionsData] = useState<Tables<'question'>[]>([]);
    const { organization } = useOrganizationStore();

    const schema = z.object({
        name: z.string({ message: "Field is required" }).min(1, { message: "Field is required" }),
        description: z.string().optional(),
        crm_system: z.string().min(1, { message: "Field is required" }),
    } satisfies Record<keyof Pick<Tables<'survey_bank'>, 'name' | 'description' | 'crm_system'>, unknown>);
    type schemaType = z.infer<typeof schema>;
    const form = useForm<schemaType>({ resolver: zodResolver(schema) });
    const { handleSubmit } = form;

    const { mutateAsync: addSurveyToSurveyBank, isPending: isPendingAddSurveyToSurveyBank } = useAddSurveyToSurveyBank();
    const { mutateAsync: saveSurveyAsTemplate, isPending: isPendingSaveSurveyAsTemplate } = useSaveSurveyAsTemplate();
    const { mutateAsync: addQuestionsToSurveyBankQuestionBank, isPending: isPendingAddQuestionsToSurveyBankQuestionBank } = useAddQuestionsToSurveyBankQuestionBank();
    const { mutateAsync: editSurveyInSurveyBank, isPending: isPendingEditSurveyInSurveyBank } = useEditSurveyInSurveyBank();
    const { mutateAsync: syncSurveyWithTemplate, isPending: isPendingSyncSurveyWithTemplate } = useSyncSurveysWithTemplate()
    const { mutateAsync: deleteQuestionsFromSurveyBankQuestionBank, isPending: isPendingDeleteQuestionsFromSurveyBankQuestionBank } = useDeleteQuestionsFromSurveyBankQuestionBank();
    const { mutateAsync: checkSyncSurveysWithTemplate } = useCheckSyncSurveysWithTemplate({ surveyTemplateId: surveyId || '' });

    // Edit existing survey template logic
    const { data: surveyData, isLoading } = useGetSingleSurveyFromBank({ surveyId: surveyId || '' });
    // console.log("surveyData", surveyData);
    useEffect(() => {
        if (surveyData) {
            form.reset({
                name: surveyData.name,
                description: surveyData.description || '',
                crm_system: surveyData.crm_system || DEFAULT_CRM_SYSTEM,
            });

            setSurveyQuestionsData(surveyData.survey_bank_question_bank.map(question => ({
                ...question,
                id: question.id || crypto.randomUUID(),
                isNew: false,
            })));
        }
    }, [surveyData])
    // End of edit existing survey template logic

    const onSubmitSurvey = (sync: boolean) => async (values: schemaType) => {
        if (!surveyQuestionsData.length) {
            const userDecision = await DecisionModal({
                message: 'You are about to add a survey template without any questions. Are you sure you want to proceed?',
                confirmButtonText: 'Save Without Adding Questions',
                cancelButtonText: 'Cancel'
            });
            if (!userDecision.isConfirmed) return;
        }

        try {
            if (!surveyId) {
                // Create a new survey template
                const surveyRequestBody = {
                    name: values.name,
                    description: values.description,
                    archived: false,
                    crm_id: null,
                    crm_config_id: null,
                    crm_system: values.crm_system,
                    organisation_id: organization?.id,
                    survey_ref_id: null,
                }

                const surveyBankResponse = await addSurveyToSurveyBank(surveyRequestBody);
                const surveyId = surveyBankResponse?.id;
                if (surveyId) {
                    const questionsToSave = surveyQuestionsData.map(({ isNew, ...question }) => ({
                        ...question,

                        crm_id: null,
                        organisation_id: organization?.id,
                        crm_system: values.crm_system,
                        survey_bank_id: surveyId,
                    }));

                    addQuestionsToSurveyBankQuestionBank(questionsToSave)
                        .then(() => {
                            SuccessPopup("Survey template created successfully");
                            navigate(`/organization/${organization?.id}/manage-organization/survey-bank?crmSystem=${values.crm_system}`);
                        })
                        .catch((error) => {
                            if (error instanceof Error) {
                                console.error(error);
                                ErrorPopup(error.message);
                            } else {
                                ErrorPopup("Something went wrong");
                            }
                        });
                }
            } else {
                if (!surveyData) return;
                const surveyRequestBody = {
                    id: surveyData.id,
                    name: values.name,
                    description: values.description,
                    archived: false,
                    crm_id: surveyData.crm_id,
                    crm_config_id: surveyData.crm_config_id,
                    crm_system: values.crm_system as CrmSystemIds,
                    organisation_id: surveyData.organisation_id,
                    survey_ref_id: surveyData.survey_ref_id,
                }
                const questionsToSave = surveyQuestionsData.map(({ isNew, created_at, ...question }) => ({
                    ...question,
                    organisation_id: organization?.id,
                    crm_system: values.crm_system as CrmSystemIds,
                    survey_bank_id: surveyId,
                    field_type_context_stack: question.field_type_context_stack || [],
                }));
                if (sync) {
                    const checkSyncSurveyTemplate = await checkSyncSurveysWithTemplate({
                        surveyTemplateId: surveyData.id,
                        changes: {
                            ...surveyRequestBody,
                            question: questionsToSave
                        },
                    })
                    console.log(checkSyncSurveyTemplate)
                    const hasDangerousChanges = checkSyncSurveyTemplate?.length > 0;
                    const checkResult = hasDangerousChanges ? await dangerousSyncDialog({
                        checkResult: checkSyncSurveyTemplate,
                    }).async() : true;
                    if (checkResult) {
                        await syncSurveyWithTemplate({ surveyTemplateId: surveyData.id })
                            .then(() => {
                                SuccessPopup(hasDangerousChanges ? "All surveys linked to this template have been synced" : "Survey template saved and linked surveys are already up to date");
                            })
                            .catch((error) => {
                                if (error instanceof Error) {
                                    console.error(error);
                                    ErrorPopup(error.message);
                                } else {
                                    ErrorPopup("Something went wrong while syncing surveys");
                                }
                            });
                    }
                } else {
                    saveSurveyAsTemplate({
                        source: 'surveyBank',
                        surveyId: surveyId,
                        changes: surveyRequestBody,
                        suffixNameIfDidntChange: "(Copy)"
                    })
                        .then(() => {
                            SuccessPopup("Survey template saved as new template successfully");
                        })
                        .catch((error) => {
                            ErrorPopup("Something went wrong while saving as new template");
                            console.error(error);
                        });
                }
            }

        } catch (error) {
            if (error instanceof Error) {
                console.error(error);
                ErrorPopup(error.message);
            } else {
                ErrorPopup("An error occurred while saving the survey template.");
            }
        }
    }

    const addQuestionModalSubmitHandler = (questionBody: Tables<'question'>) => {
        setSurveyQuestionsData(prevDate => [...prevDate, { ...questionBody, id: crypto.randomUUID(), isNew: questionBody.id ? false : true }]);
    }

    const onRemoveQuestion = async (questionId: string) => {
        const userDecision = await DecisionModal({ title: "Delete Question", confirmButtonText: "Delete", message: 'Are you sure you want to delete this question?' });
        if (!userDecision.isConfirmed) return;

        setSurveyQuestionsData(prev => prev.filter(question => question.id !== questionId));

        // setSurveyQuestionsData(prevData => prevData.filter(question => question.id !== questionId).map(question => {
        //     if (question.selected_configuration?.conditionals?.where?.or) {
        //         question.selected_configuration.conditionals.where.or = question.selected_configuration.conditionals.where.or.map(condition => {
        //             if (condition.and) {
        //                 condition.and = condition.and.filter(cond => !Object.keys(cond).includes(questionId));
        //             }
        //             return condition;
        //         });
        //     }
        //     return question;
        // }));
    }

    const [openEditQuestionModal, setOpenEditQuestionModal] = useState(false);
    const [selectedQuestionData, setSelectedQuestionData] = useState<Tables<'question'> | null>(null);
    const onEditQuestion = (questionId: string) => {
        setOpenEditQuestionModal(true);
        const selectedQuestion = surveyQuestionsData.find(question => question.id === questionId);
        if (selectedQuestion) {
            setSelectedQuestionData(selectedQuestion);
        }
    }
    const onSubmitEditQuestion = (questionBody: Tables<'question'>) => {
        setSurveyQuestionsData(prevData => prevData.map(question => {
            if (question.id === questionBody.id) {
                return {
                    ...question,
                    ...questionBody,
                    // isNew: false, // Mark as not new
                };
            }
            return question;
        }));
        setOpenEditQuestionModal(false);
    }

    // Evaluate questions conditionals
    const fullGroupedQuestions = groupQuestionsByDependencies(surveyQuestionsData);

    const isPending = isPendingAddSurveyToSurveyBank || isPendingSaveSurveyAsTemplate || isPendingAddQuestionsToSurveyBankQuestionBank || isLoading || isPendingEditSurveyInSurveyBank || isPendingDeleteQuestionsFromSurveyBankQuestionBank || isPendingSyncSurveyWithTemplate;

    return (
        <>
            <NavigationBarBreadCrumbs.Set
                currentPageName={surveyId ? "Edit Template" : "Add Template"}
                pages={[
                    {
                        title: "Home",
                        goTo: `/welcome`
                    },
                    {
                        title: organization?.name || "-",
                        goTo: `/organization/${organization?.id}`
                    },
                    {
                        title: "Manage Survey Templates",
                        goTo: `/organization/${organization?.id}/manage-organization/survey-bank`
                    },

                ]}
            />

            <AddQuestionModal
                show={showAddModal}
                onHide={() => setShowAddModal(false)}
                onSubmit={addQuestionModalSubmitHandler}

                crm={{ crm_system: form.watch('crm_system') || DEFAULT_CRM_SYSTEM }}
                crmProject={{}} // TODO: check if this is needed
                crmConfig={{}} // TODO: check if this is needed

                questions={surveyQuestionsData}
                defaultStep={3}
            />

            <EditQuestionModal
                show={openEditQuestionModal}
                onHide={() => setOpenEditQuestionModal(false)}
                onSubmit={onSubmitEditQuestion}

                questionData={selectedQuestionData}
                questionsFromOutside={surveyQuestionsData}

                crm={{ crm_system: form.watch('crm_system') || DEFAULT_CRM_SYSTEM }}
                crmProject={{}} // TODO: check if this is needed
                crmConfig={{}} // TODO: check if this is needed
            />

            <div className="flex flex-col h-full">
                <div className="flex flex-col flex-1 gap-2">
                    <ScrollArea className="pe-4" style={{ height: "calc(100vh - 182px)" }} type="always">
                        <div className="flex flex-col gap-2">
                            <div className="flex flex-col gap-2">
                                <Form {...form}>
                                    <form className="flex flex-wrap">
                                        <div className="flex flex-col w-full gap-2 px-1 sm:w-6/12">
                                            <FormField
                                                control={form.control}
                                                name="name"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Survey Name</FormLabel>
                                                        <FormControl>
                                                            <Input {...field} disabled={isPending} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                            <FormField
                                                control={form.control}
                                                name="crm_system"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>CRM System (optional)</FormLabel>
                                                        <FormControl>
                                                            <SelectCrmSystemSanitizedCombobox
                                                                mode='single'
                                                                selectedValue={field.value || ""}
                                                                setSelectedValue={field.onChange}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>

                                        <div className="flex flex-col w-full gap-2 px-1 sm:w-6/12">
                                            <FormField
                                                control={form.control}
                                                name="description"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Description</FormLabel>
                                                        <FormControl>
                                                            <Textarea {...field} disabled={isPending} />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    </form>
                                </Form>

                                <Separator className="mt-2" />

                                <div className="flex items-center justify-between">
                                    <p className="text-xs">Questions</p>

                                    <div className="flex justify-center gap-2">
                                        <Button className="ml-auto" size="sm" onClick={() => setShowAddModal(true)} disabled={isPending}>
                                            Add Question
                                        </Button>
                                    </div>
                                </div>

                                <div className="flex flex-col w-full h-full gap-2 min-h-52 grow">
                                    {isPending && (surveyQuestionsData?.length === 0) && (
                                        <div className="flex items-center justify-center h-full">
                                            <Loader size="16" className={`animate-spin`} />
                                        </div>
                                    )}

                                    {!isPending && (surveyQuestionsData?.length === 0) && (
                                        <div className="flex items-center justify-center h-full">
                                            <p className="text-sm text-muted-foreground">No questions added yet. Click on the "Add Question" button above to add a question.</p>
                                        </div>
                                    )}

                                    {fullGroupedQuestions?.map((question) => (
                                        <QuestionRow
                                            key={question.id}
                                            question={question}
                                            onEditQuestion={onEditQuestion}
                                            onRemoveQuestion={onRemoveQuestion}
                                        />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </ScrollArea>
                </div>

                <div className="flex justify-end mt-3 space-x-2">
                    <Button asChild={!isPending} size='sm' variant="secondary" disabled={isPending}>
                        {isPending ? (
                            <>Back</>
                        ) : (
                            <Link to={`/organization/${organization?.id}/manage-organization/survey-bank?crmSystem=${surveyData?.crm_system || DEFAULT_CRM_SYSTEM}`}>
                                Back
                            </Link>
                        )}
                    </Button>
                    <Button size='sm' onClick={handleSubmit(onSubmitSurvey(false))} disabled={isPending}>
                        {isPending && (<Loader size="16" className={`animate-spin`} />)} Save as new template
                    </Button>
                    <Button size='sm' onClick={handleSubmit(onSubmitSurvey(true))} disabled={isPending}>
                        {isPending && (<Loader size="16" className={`animate-spin`} />)} Save template & sync surveys
                    </Button>
                </div>
            </div>
        </>
    )
}
